/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { PasswordService } from 'app/core/services/password-service';
import { UserService } from 'app/core/services/user-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { forceNull } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveLocalUser extends ComponentBase {
  ////****************** Fields and Properties ************************************////
  protected user: Core.User | null = null;

  protected validateForm = validate;

  @bindable.string({ defaultBindingMode: bindingMode.twoWay })
  protected password: string = "";

  protected confirmPassword: string = "";

  ////****************** Element References ***************************************////


  ////****************** Lifecycle events (in order of execution) *****************////
  constructor(
    private readonly userService: UserService,
    private readonly toaster: Toaster,
    protected readonly passwordService: PasswordService,
    ...rest: any[]) {
    super(...rest);
  }

  ////****************** Events ***************************************************////
  protected bind(): void {
    if(!this.user) this.clear();
  }


  ////****************** Functions ************************************************////
  public clear(): void {
    this.user = {
      id: null,
      isActive: true,
      code: null,
      name: {
        first: null,
        last: null
      },
      title: null,
      department: null,
      emailAddress: null,
      phoneNumber: {
        primary: null,
        cell: null
      },
      twoFactorAuthentication: {
        optedIn: false
      }
    };
    this.password = "";
    this.confirmPassword = "";
  }

  // Parse user and build new user
  protected buildUser(): Core.User {
    const user = this.user;
    let saveUser: Core.User = {};
    if(!!user) {
      saveUser = {
        id: null,
        isActive: true,
        code: forceNull(user.code),
        name: {
          first: forceNull(user.name?.first),
          last: forceNull(user.name?.last)
        },
        title: forceNull(user.title),
        department: forceNull(user.department),
        emailAddress: forceNull(user.emailAddress),
        // Phone Numbers
        phoneNumber: {
          primary: user.phoneNumber?.primary?.number ? user.phoneNumber.primary : null,
          cell: user.phoneNumber?.cell?.number ? user.phoneNumber.cell : null
        },
        login: {
          password: forceNull(this.password)
        },
        twoFactorAuthentication: {
          optedIn: false
        }
      };
    }

    return saveUser;
  }

  protected async submit(): Promise<void> {
    let isSuccessful = await this.saveUser();

    if(isSuccessful) {
      this.clear();
      await this.router.navigateToRoute('core.admin.user', { userId: this.user?.id });
    }

  }

  ////****************** Service Calls ********************************************////
  @apiCall
  private async saveUser(): Promise<boolean> {
    if(!this.user) return false;
    let isSuccessful = false;
    try {
      this.state.beginSave();
      const response = await this.userService.saveUser(this.buildUser());
      this.user.id = response.id;
      isSuccessful = response.isSuccessful;
    }
    catch(error) {
      throw error;
    }
    finally {
      this.toaster.standard.created('core:user.$', isSuccessful);
      this.state.endSave();
      if(isSuccessful) { await this.router.navigateToRoute('core.admin.user', { userId: this.user.id }); }
      return isSuccessful;
    }
  }
}
